import { MouseEvent } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, InlineMessage } from "chameleon/lib/components";

import { buttonNameMapper } from "checkout-experience/components/constants";
import { MandateList } from "checkout-experience/components/mandate-list/mandate-list";
import { GooglePay } from "checkout-experience/components/payment-method/pay-options/google/google-pay";
import PayPal from "checkout-experience/components/payment-method/pay-options/paypal/paypal";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import {
  marginTopClass,
  wrapperClass,
} from "checkout-experience/components/place-order/place-order.css";
import { useEnabledPaymentMethods } from "checkout-experience/hooks/use-enabled-payment-methods";
import { useIsCreditCardWithSurchargeChex } from "checkout-experience/hooks/use-is-credit-card-with-surcharge-chex";
import { useSurcharge } from "checkout-experience/hooks/use-surcharge";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PaymentDomain } from "checkout-query-client/api/types";
import { PaymentType } from "checkout-query-client/types/form-types";
import { isAltPaymentType } from "checkout-query-client/utils/helpers";

import { SendMessage } from "field-frames/src/frame/types";

interface PlaceOrderProps {
  showAction: boolean;
  isPayInProgress: boolean;
  onClick: () => Promise<void>;
  applePayComponent: JSX.Element | null;
  paymentDomain?: PaymentDomain;
}

const PlaceOrder = (props: PlaceOrderProps) => {
  const { t } = useTranslation(["combined"]);
  const {
    showAction,
    onClick,
    isPayInProgress,
    applePayComponent,
    paymentDomain,
  } = props;
  const { merchantSettings, experienceType, controlledSubmit } =
    useChexContext();
  const { paymentButtonName, orderMandates } = merchantSettings ?? {};
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { activePaymentMethodsList } = useEnabledPaymentMethods();
  const { paymentType } = paymentOption ?? {};
  const checkoutForm = useFormContext();
  const { data: isCreditCard } = useIsCreditCardWithSurchargeChex(checkoutForm);
  const surcharge = useSurcharge();

  const translatedTitle = isAltPaymentType(paymentType)
    ? t("checkoutForm.submitButton.proceedWithLpm", { lpm: t(paymentType) })
    : t(
        `checkoutForm.submitButton.${buttonNameMapper[paymentButtonName || 0]}`
      );

  const handleOnClick = async (e: MouseEvent | undefined) => {
    e?.preventDefault();
    const globalVerify = new CustomEvent<SendMessage>("action", {
      detail: { message: "validate", payload: {} },
    });
    window.dispatchEvent(globalVerify);
    // break the sync loop to allow for field frames to report updates before proceeding.
    // This really only matters for if the field frame is the last element interacted with before button click
    // It is possible for it not to emit the updated error status before processing the click
    await new Promise((resolve) => setTimeout(resolve, 1));

    return onClick();
  };

  const getActionButton = () => {
    if (showAction) {
      if (
        (activePaymentMethodsList.length === 1 &&
          activePaymentMethodsList[0].name === PaymentType.applePay) ||
        paymentType === PaymentType.applePay
      ) {
        return <>{applePayComponent}</>;
      } else if (
        (activePaymentMethodsList.length === 1 &&
          activePaymentMethodsList[0].name === PaymentType.googlePay) ||
        paymentType === PaymentType.googlePay
      ) {
        return <GooglePay paymentDomain={paymentDomain} />;
      } else if (
        (activePaymentMethodsList.length === 1 &&
          activePaymentMethodsList[0].name === PaymentType.paypal) ||
        paymentType === PaymentType.paypal
      ) {
        return (
          <PayPal
            handleOnClick={handleOnClick}
            isPayInProgress={isPayInProgress}
          />
        );
      } else {
        return (
          <Button
            id={"btn-place-order"}
            testId={"btn-place-order"}
            position="left"
            text={translatedTitle}
            isLoading={isPayInProgress}
            isDisabled={isPayInProgress}
            onClick={handleOnClick}
          />
        );
      }
    } else {
      return null;
    }
  };

  return (
    <>
      <span
        data-testid="place-order-section-header"
        id="placeOrderSectionHeaderId"
        hidden
      >
        {translatedTitle}
      </span>
      <div
        data-testid="mandate-btn-wrapper"
        className={`${wrapperClass} ${experienceType === "dropInUI" ? marginTopClass : ""}`}
      >
        {isCreditCard &&
          experienceType === "dropInUI" &&
          surcharge?.priceDisclaimer && (
            <InlineMessage
              message={surcharge?.priceDisclaimer}
              id={"placeOrderSurchargePriceDisclaimer"}
            />
          )}
        <MandateList
          mandates={orderMandates || []}
          paymentType={paymentType}
          placement={"placeOrder"}
        />
        {!controlledSubmit ? getActionButton() : null}
      </div>
    </>
  );
};

export default PlaceOrder;
